import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';

export interface RegistrationData {
  email: string;
  username: string;
  password: string;
  full_name: string;
  plan?: string;
}

export interface RegistrationStep {
  step: 'auth' | 'email-verification' | 'plan-selection' | 'payment' | 'api-access';
  data: Partial<RegistrationData>;
}

export function useRegistrationFlow() {
  const [currentStep, setCurrentStep] = useState<RegistrationStep['step']>('auth');
  const [registrationData, setRegistrationData] = useState<RegistrationData>({
    email: '',
    username: '',
    password: '',
    full_name: '',
    plan: undefined
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Charger les données depuis le localStorage au démarrage
  useEffect(() => {
    const savedData = localStorage.getItem('registration_data');
    const savedStep = localStorage.getItem('registration_step') as RegistrationStep['step'];
    
    if (savedData && savedStep) {
      setRegistrationData(JSON.parse(savedData));
      setCurrentStep(savedStep);
    }
  }, []);

  // Sauvegarder les données à chaque changement
  useEffect(() => {
    localStorage.setItem('registration_data', JSON.stringify(registrationData));
    localStorage.setItem('registration_step', currentStep);
  }, [registrationData, currentStep]);

  const updateRegistrationData = (data: Partial<RegistrationData>) => {
    setRegistrationData(prev => ({ ...prev, ...data }));
  };

  const goToStep = (step: RegistrationStep['step']) => {
    setCurrentStep(step);
    
    // Retourner les informations de redirection au lieu de naviguer directement
    switch (step) {
      case 'auth':
        return { redirectTo: '/auth' };
      case 'email-verification':
        return { redirectTo: `/auth/verify-email-waiting?email=${registrationData.email}` };
      case 'plan-selection':
        return { redirectTo: '/auth/plan-selection' };
      case 'payment':
        return { redirectTo: '/auth/payment' };
      case 'api-access':
        return { redirectTo: '/pipeline' };
      default:
        return { redirectTo: '/auth' };
    }
  };

  const registerUser = async (data: RegistrationData) => {
    setIsLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: data.email, 
          username: data.username, 
          password: data.password, 
          confirm_password: data.password, 
          full_name: data.full_name, 
          plan: 'free' // Toujours créer avec plan gratuit, sélection après vérification
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Erreur d\'inscription');
      }

      const responseData = await response.json();
      
      // Stocker le token temporaire
      localStorage.setItem('access_token', responseData.access_token);
      
      // Mettre à jour les données d'inscription
      updateRegistrationData(data);
      
      // Retourner les informations de redirection
      const redirectInfo = goToStep('email-verification');
      return { ...responseData, ...redirectInfo };
      
    } catch (error) {
      console.error('Erreur d\'inscription:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const checkEmailVerificationStatus = async (): Promise<boolean> => {
    try {
      const response = await fetch(API_ENDPOINTS.EMAIL_VERIFICATION_STATUS(registrationData.email), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const status = await response.json();
        return status.email_verified && status.is_active;
      }
      return false;
    } catch (error) {
      console.error('Erreur lors de la vérification du statut:', error);
      return false;
    }
  };

  const proceedToNextStep = async () => {
    switch (currentStep) {
      case 'email-verification':
        // Vérifier si l'email est vérifié
        const isVerified = await checkEmailVerificationStatus();
        if (isVerified) {
          // Aller directement à l'API après vérification (pas de sélection de plan)
          goToStep('api-access');
        }
        break;
        
      case 'plan-selection':
        // Si plan gratuit, aller directement à l'API
        if (registrationData.plan === 'free') {
          goToStep('api-access');
        } else {
          // Si plan payant, aller au paiement
          goToStep('payment');
        }
        break;
        
      case 'payment':
        // Après paiement réussi, aller à l'API
        goToStep('api-access');
        break;
    }
  };

  const clearRegistrationData = () => {
    localStorage.removeItem('registration_data');
    localStorage.removeItem('registration_step');
    setRegistrationData({
      email: '',
      username: '',
      password: '',
      full_name: '',
      plan: undefined
    });
    setCurrentStep('auth');
  };

  return {
    currentStep,
    registrationData,
    isLoading,
    updateRegistrationData,
    goToStep,
    registerUser,
    checkEmailVerificationStatus,
    proceedToNextStep,
    clearRegistrationData
  };
}
